/**
 * 语料管理 Tab
 * 列表、上传、导入、删除
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Badge } from '@/shared/components/ui/badge'
import {
  FileText,
  Upload,
  Download,
  Plus,
  Trash2,
  Search,
  Filter,
  Loader2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Edit,
} from 'lucide-react'
import { useToast } from '@/shared/hooks/useToast'
import { IdentityApiService } from '@/modules/identity/services'
import { CorpusTypeBadge } from '@/modules/identity/components'
import type {
  Corpus,
  CorpusType,
  CorpusSummary,
  CorpusListParams,
} from '@/modules/identity/types'
import { CORPUS_TYPE_CONFIG } from '@/modules/identity/types'
import { FacetApiService } from '@/modules/identity/services'
import { SCENE_TAG_CONFIG } from '@/modules/identity/types/facet'
import type { TargetPerson } from '@/modules/identity/types/facet'

interface CorpusTabProps {
  identityId: string
  onCorpusChange?: () => void
}

export function CorpusTab({ identityId, onCorpusChange }: CorpusTabProps) {
  const { showSuccess, showError } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 列表状态
  const [corpusList, setCorpusList] = useState<Corpus[]>([])
  const [summary, setSummary] = useState<CorpusSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState('')
  const [typeFilter, setTypeFilter] = useState<CorpusType | 'all'>('all')
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 1,
  })

  // 选择状态
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // 弹窗状态
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  
  // 操作状态
  const [adding, setAdding] = useState(false)
  const [importing, setImporting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [editing, setEditing] = useState(false)
  
  // 编辑表单（v9.2.2 新增）
  const [editingCorpus, setEditingCorpus] = useState<Corpus | null>(null)
  const [editForm, setEditForm] = useState<{
    name: string
    target_person: string
    target_person_aliases: string
  }>({
    name: '',
    target_person: '',
    target_person_aliases: '',
  })

  // 添加表单（v9.2 更新：新增 name 字段，移除 scene_tag 字段）
  const [addForm, setAddForm] = useState<{
    name: string               // 🆕 v9.2 用户自定义名称（必填）
    type: CorpusType
    content: string
    source?: string
    target_person: string      // 被学习人名称（必填）
    // 注意：v9.2 移除了 scene_tag 字段，场景关联在单独接口中进行
  }>({
    name: '',
    type: 'other',
    content: '',
    source: '',
    target_person: '',
  })

  // 被学习人列表（用于 Combobox）
  const [targetPersons, setTargetPersons] = useState<TargetPerson[]>([])
  const [, setLoadingTargetPersons] = useState(false)

  // 导入状态
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importResult, setImportResult] = useState<{
    imported_count: number
    failed_count: number
    failed_rows?: number[]
    failed_reasons?: string[]
  } | null>(null)

  // ==================== 数据加载 ====================
  const loadCorpus = useCallback(async () => {
    setLoading(true)
    try {
      const params: CorpusListParams = {
        page: pagination.page,
        page_size: pagination.page_size,
      }
      if (searchText) {
        params.search = searchText
      }
      if (typeFilter !== 'all') {
        params.type = typeFilter
      }

      const response = await IdentityApiService.listCorpus(identityId, params)
      // 防御性编程：后端可能返回 items 或 data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const responseData = response.data as any
      const corpusData = responseData?.data || responseData?.items || []
      setCorpusList(corpusData)
      setSummary(responseData?.summary || null)
      setPagination(prev => ({
        ...prev,
        total: responseData?.pagination?.total || 0,
        total_pages: responseData?.pagination?.total_pages || 1,
      }))
      setSelectedIds(new Set())
    } catch (error) {
      console.error('Failed to load corpus:', error)
      showError('无法加载语料列表', '加载失败')
    } finally {
      setLoading(false)
    }
  }, [identityId, pagination.page, pagination.page_size, searchText, typeFilter, showError])

  useEffect(() => {
    loadCorpus()
  }, [loadCorpus])

  // 加载被学习人列表（v9.1）
  const loadTargetPersons = useCallback(async () => {
    setLoadingTargetPersons(true)
    try {
      const response = await FacetApiService.listTargetPersons(identityId)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const responseData = response.data as any
      const items = responseData?.items || []
      setTargetPersons(items)
    } catch (error) {
      console.error('Failed to load target persons:', error)
      // 不显示错误，这是可选功能
    } finally {
      setLoadingTargetPersons(false)
    }
  }, [identityId])

  useEffect(() => {
    loadTargetPersons()
  }, [loadTargetPersons])

  // ==================== 选择操作 ====================
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(corpusList.map(c => c.id)))
    } else {
      setSelectedIds(new Set())
    }
  }, [corpusList])

  const handleSelectOne = useCallback((id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (checked) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }, [])

  // ==================== 添加语料 ====================
  const handleAdd = useCallback(async () => {
    // v9.2 验证：name 和 target_person 必填
    if (!addForm.name.trim()) {
      showError('请输入语料名称', '验证失败')
      return
    }
    if (!addForm.target_person.trim()) {
      showError('请输入被学习人', '验证失败')
      return
    }
    if (!addForm.content.trim()) {
      showError('请输入语料内容', '验证失败')
      return
    }

    setAdding(true)
    try {
      await IdentityApiService.uploadCorpus(identityId, {
        // v9.2 更新：新增 name 字段，移除 scene_tag 字段
        name: addForm.name.trim(),
        target_person: addForm.target_person.trim(),
        corpus: [{
          type: addForm.type,
          content: addForm.content.trim(),
          metadata: addForm.source ? { source: addForm.source } : undefined,
        }],
      })
      showSuccess('语料已添加', '添加成功')
      setAddDialogOpen(false)
      setAddForm({ name: '', type: 'other', content: '', source: '', target_person: '' })
      loadCorpus()
      loadTargetPersons() // 刷新被学习人列表
      onCorpusChange?.()
    } catch (error) {
      console.error('Failed to add corpus:', error)
      showError('添加失败', '操作失败')
    } finally {
      setAdding(false)
    }
  }, [identityId, addForm, showSuccess, showError, loadCorpus, loadTargetPersons, onCorpusChange])

  // ==================== 导入语料 ====================
  const handleImportFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // 验证文件类型
      const validTypes = ['.csv', '.xlsx', '.xls']
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
      if (!validTypes.includes(ext)) {
        showError('请选择 CSV 或 Excel 文件', '文件类型错误')
        return
      }
      setImportFile(file)
      setImportResult(null)
    }
  }

  const handleImport = useCallback(async () => {
    if (!importFile) return

    setImporting(true)
    try {
      const response = await IdentityApiService.importCorpus(identityId, importFile)
      setImportResult(response.data)
      
      if (response.data.failed_count === 0) {
        showSuccess(`成功导入 ${response.data.imported_count} 条语料`, '导入成功')
        setImportDialogOpen(false)
        setImportFile(null)
        loadCorpus()
        onCorpusChange?.()
      }
    } catch (error) {
      console.error('Failed to import corpus:', error)
      showError('导入失败', '操作失败')
    } finally {
      setImporting(false)
    }
  }, [identityId, importFile, showSuccess, showError, loadCorpus, onCorpusChange])

  // ==================== 编辑语料（v9.2.2 新增）====================
  const handleEditClick = useCallback((corpus: Corpus) => {
    setEditingCorpus(corpus)
    setEditForm({
      name: corpus.name || '',
      target_person: corpus.target_person || '',
      target_person_aliases: '', // 如果后端返回 aliases，可以在这里填充
    })
    setEditDialogOpen(true)
  }, [])

  const handleEditSave = useCallback(async () => {
    if (!editingCorpus) return
    
    if (!editForm.name.trim()) {
      showError('请输入语料名称', '验证失败')
      return
    }

    setEditing(true)
    try {
      const updateData: {
        name?: string
        target_person?: string
        target_person_aliases?: string[]
      } = {
        name: editForm.name.trim(),
      }
      
      if (editForm.target_person.trim()) {
        updateData.target_person = editForm.target_person.trim()
      }
      
      if (editForm.target_person_aliases.trim()) {
        updateData.target_person_aliases = editForm.target_person_aliases
          .split(/[,，]/)
          .map(s => s.trim())
          .filter(s => s.length > 0)
      }

      await IdentityApiService.updateCorpus(identityId, editingCorpus.id, updateData)
      showSuccess('语料已更新', '编辑成功')
      setEditDialogOpen(false)
      setEditingCorpus(null)
      loadCorpus()
      loadTargetPersons()
    } catch (error) {
      console.error('Failed to update corpus:', error)
      showError('更新失败', '操作失败')
    } finally {
      setEditing(false)
    }
  }, [identityId, editingCorpus, editForm, showSuccess, showError, loadCorpus, loadTargetPersons])

  // ==================== 删除语料 ====================
  const handleDeleteClick = useCallback(() => {
    if (selectedIds.size === 0) {
      showError('请先选择要删除的语料', '操作提示')
      return
    }
    setDeleteDialogOpen(true)
  }, [selectedIds.size, showError])

  const handleDeleteConfirm = useCallback(async () => {
    if (selectedIds.size === 0) return

    setDeleting(true)
    try {
      await IdentityApiService.batchDeleteCorpus(identityId, Array.from(selectedIds))
      showSuccess(`已删除 ${selectedIds.size} 条语料`, '删除成功')
      setDeleteDialogOpen(false)
      loadCorpus()
      onCorpusChange?.()
    } catch (error) {
      console.error('Failed to delete corpus:', error)
      showError('删除失败', '操作失败')
    } finally {
      setDeleting(false)
    }
  }, [identityId, selectedIds, showSuccess, showError, loadCorpus, onCorpusChange])

  // ==================== 渲染 ====================
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              语料管理
            </CardTitle>
            <CardDescription>
              管理用于人格学习的语料数据
              {summary && (
                <span className="ml-2 text-violet-600">
                  共 {summary.total_count} 条
                </span>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setImportDialogOpen(true)}
            >
              <Download className="h-4 w-4 mr-2" />
              批量导入
            </Button>
            <Button size="sm" onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              添加语料
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 统计信息 */}
        {summary && summary.total_count > 0 && (
          <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg">
            {Object.entries(summary.by_type).map(([type, count]) => (
              count > 0 && (
                <Badge key={type} variant="secondary" className="gap-1">
                  {CORPUS_TYPE_CONFIG[type as CorpusType]?.icon}
                  {CORPUS_TYPE_CONFIG[type as CorpusType]?.label}: {count}
                </Badge>
              )
            ))}
          </div>
        )}

        {/* 搜索和筛选 */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="搜索语料内容..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value)
                setPagination(prev => ({ ...prev, page: 1 }))
              }}
              className="pl-10"
            />
          </div>
          <Select 
            value={typeFilter} 
            onValueChange={(v) => {
              setTypeFilter(v as CorpusType | 'all')
              setPagination(prev => ({ ...prev, page: 1 }))
            }}
          >
            <SelectTrigger className="w-[140px]">
              <Filter className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="类型筛选" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部类型</SelectItem>
              {Object.entries(CORPUS_TYPE_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>
                  {config.icon} {config.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={() => loadCorpus()}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* 批量操作栏 */}
        {selectedIds.size > 0 && (
          <div className="flex items-center justify-between p-2 bg-violet-50 rounded-lg">
            <span className="text-sm text-violet-700">
              已选择 {selectedIds.size} 条语料
            </span>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDeleteClick}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              删除选中
            </Button>
          </div>
        )}

        {/* 语料列表 */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-violet-600" />
            <p className="text-slate-500">加载中...</p>
          </div>
        ) : corpusList.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="mb-2">
              {searchText || typeFilter !== 'all' ? '没有找到匹配的语料' : '暂无语料'}
            </p>
            <p className="text-sm mb-4">
              {searchText || typeFilter !== 'all' ? '尝试调整搜索条件' : '添加语料以开始人格学习'}
            </p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedIds.size === corpusList.length && corpusList.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead className="w-48">名称</TableHead>
                  <TableHead className="w-20">类型</TableHead>
                  <TableHead>内容预览</TableHead>
                  <TableHead className="w-28">已关联场景</TableHead>
                  <TableHead className="w-36">添加时间</TableHead>
                  <TableHead className="w-20">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {corpusList.map((corpus) => (
                  <TableRow key={corpus.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(corpus.id)}
                        onCheckedChange={(checked) => handleSelectOne(corpus.id, !!checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm line-clamp-1">
                          {corpus.name || `${CORPUS_TYPE_CONFIG[corpus.type]?.label || corpus.type}_${corpus.created_at?.slice(0, 10)}`}
                        </p>
                        {corpus.target_person && (
                          <p className="text-xs text-slate-400">被学习人：{corpus.target_person}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <CorpusTypeBadge type={corpus.type} />
                    </TableCell>
                    <TableCell>
                      <p className="line-clamp-2 text-sm text-slate-600">
                        {corpus.content?.slice(0, 80)}
                        {corpus.content && corpus.content.length > 80 && '...'}
                      </p>
                    </TableCell>
                    <TableCell>
                      {corpus.scenes && corpus.scenes.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {corpus.scenes.map(scene => (
                            <span 
                              key={scene} 
                              className="text-sm" 
                              title={SCENE_TAG_CONFIG[scene]?.label}
                            >
                              {SCENE_TAG_CONFIG[scene]?.icon}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">未关联</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(corpus.created_at).toLocaleString('zh-CN', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      })}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-500 hover:text-violet-600 hover:bg-violet-50"
                        onClick={() => handleEditClick(corpus)}
                        title="编辑"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* 分页 */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              上一页
            </Button>
            <span className="text-sm text-slate-500">
              {pagination.page} / {pagination.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              下一页
            </Button>
          </div>
        )}
      </CardContent>

      {/* 添加语料弹窗（v9.2 更新：新增 name，移除 scene_tag） */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>添加语料</DialogTitle>
            <DialogDescription>
              添加用于人格学习的语料，上传后可在「场景人格」中添加到对应场景进行学习
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* 🆕 v9.2 新增：语料名称 */}
            <div className="space-y-2">
              <Label>语料名称 *</Label>
              <Input
                value={addForm.name}
                onChange={(e) => setAddForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="如：工作会议记录-2025年1月"
              />
              <p className="text-xs text-slate-400">
                给语料起一个容易识别的名称，方便后续在场景中选择
              </p>
            </div>

            {/* 被学习人 */}
            <div className="space-y-2">
              <Label>被学习人 *</Label>
              {targetPersons.length > 0 ? (
                <Select
                  value={addForm.target_person || ''}
                  onValueChange={(v) => setAddForm(prev => ({ ...prev, target_person: v === '__new__' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择或输入被学习人" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4}>
                    {targetPersons.map(tp => (
                      <SelectItem key={tp.name} value={tp.name}>
                        {tp.name} ({tp.corpus_count} 条语料)
                      </SelectItem>
                    ))}
                    <SelectItem value="__new__">+ 新建被学习人...</SelectItem>
                  </SelectContent>
                </Select>
              ) : null}
              {/* 新建或列表为空时显示输入框 */}
              {(targetPersons.length === 0 || addForm.target_person === '') && (
                <Input
                  value={addForm.target_person || ''}
                  onChange={(e) => setAddForm(prev => ({ ...prev, target_person: e.target.value }))}
                  placeholder="输入被学习人名称（如：张三）"
                />
              )}
              <p className="text-xs text-slate-400">
                语料中要学习的目标人物，系统会提取此人的发言用于学习
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>语料类型</Label>
                <Select
                  value={addForm.type}
                  onValueChange={(v) => setAddForm(prev => ({ ...prev, type: v as CorpusType }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CORPUS_TYPE_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.icon} {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>来源（可选）</Label>
                <Input
                  value={addForm.source || ''}
                  onChange={(e) => setAddForm(prev => ({ ...prev, source: e.target.value }))}
                  placeholder="如：微博"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>内容 *</Label>
              <Textarea
                value={addForm.content}
                onChange={(e) => setAddForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="输入语料内容..."
                rows={5}
                maxLength={2000}
              />
              <p className="text-xs text-slate-400">
                {addForm.content.length}/2000
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              取消
            </Button>
            <Button 
              onClick={handleAdd} 
              disabled={adding || !addForm.name.trim() || !addForm.target_person.trim()}
            >
              {adding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              添加
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 批量导入弹窗 */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => {
        setImportDialogOpen(open)
        if (!open) {
          setImportFile(null)
          setImportResult(null)
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>批量导入语料</DialogTitle>
            <DialogDescription>
              上传 CSV 或 Excel 文件批量导入语料
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* 文件上传 */}
            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-violet-300 hover:bg-violet-50/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleImportFileSelect}
                className="hidden"
              />
              {importFile ? (
                <div>
                  <CheckCircle className="h-10 w-10 mx-auto mb-3 text-green-500" />
                  <p className="font-medium">{importFile.name}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {(importFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              ) : (
                <div>
                  <Upload className="h-10 w-10 mx-auto mb-3 text-slate-400" />
                  <p className="font-medium text-slate-600">点击选择文件</p>
                  <p className="text-sm text-slate-400 mt-1">
                    支持 CSV、Excel 格式
                  </p>
                </div>
              )}
            </div>

            {/* 文件格式说明 */}
            <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
              <p className="font-medium mb-1">CSV 文件格式：</p>
              <code className="text-[10px] block bg-white p-2 rounded">
                type,content,metadata.source,metadata.date<br/>
                weibo,"内容示例",微博,2025-01-20
              </code>
            </div>

            {/* 导入结果 */}
            {importResult && (
              <div className={`p-4 rounded-lg ${importResult.failed_count > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {importResult.failed_count > 0 ? (
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                  <span className="font-medium">
                    导入完成
                  </span>
                </div>
                <p className="text-sm">
                  成功：{importResult.imported_count} 条，
                  失败：{importResult.failed_count} 条
                </p>
                {importResult.failed_rows && importResult.failed_rows.length > 0 && (
                  <div className="mt-2 text-xs text-amber-600">
                    <p>失败行号：{importResult.failed_rows.join(', ')}</p>
                    {importResult.failed_reasons && (
                      <p>原因：{importResult.failed_reasons.join('; ')}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              关闭
            </Button>
            <Button
              onClick={handleImport}
              disabled={!importFile || importing}
            >
              {importing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              开始导入
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认弹窗 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除选中的 {selectedIds.size} 条语料吗？此操作无法撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 编辑语料弹窗（v9.2.2 新增）*/}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open)
        if (!open) {
          setEditingCorpus(null)
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>编辑语料</DialogTitle>
            <DialogDescription>
              修改语料的名称和被学习人信息
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* 语料名称 */}
            <div className="space-y-2">
              <Label>语料名称 *</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="如：工作会议记录-2025年1月"
              />
            </div>

            {/* 被学习人 */}
            <div className="space-y-2">
              <Label>被学习人</Label>
              {targetPersons.length > 0 ? (
                <Select
                  value={editForm.target_person || ''}
                  onValueChange={(v) => setEditForm(prev => ({ ...prev, target_person: v === '__new__' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择或输入被学习人" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4}>
                    {targetPersons.map(tp => (
                      <SelectItem key={tp.name} value={tp.name}>
                        {tp.name} ({tp.corpus_count} 条语料)
                      </SelectItem>
                    ))}
                    <SelectItem value="__new__">+ 新建被学习人...</SelectItem>
                  </SelectContent>
                </Select>
              ) : null}
              {/* 新建或列表为空时显示输入框 */}
              {(targetPersons.length === 0 || editForm.target_person === '') && (
                <Input
                  value={editForm.target_person || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, target_person: e.target.value }))}
                  placeholder="输入被学习人名称（如：张三）"
                />
              )}
            </div>

            {/* 被学习人别名 */}
            <div className="space-y-2">
              <Label>别名（可选）</Label>
              <Input
                value={editForm.target_person_aliases}
                onChange={(e) => setEditForm(prev => ({ ...prev, target_person_aliases: e.target.value }))}
                placeholder="多个别名用逗号分隔，如：小张, 张老师"
              />
              <p className="text-xs text-slate-400">
                被学习人在语料中可能出现的其他称呼
              </p>
            </div>

            {/* 只读信息 */}
            {editingCorpus && (
              <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500">类型：</span>
                  <CorpusTypeBadge type={editingCorpus.type} />
                </div>
                <div className="text-sm">
                  <span className="text-slate-500">内容预览：</span>
                  <p className="text-slate-600 mt-1 line-clamp-3">
                    {editingCorpus.content?.slice(0, 150)}
                    {editingCorpus.content && editingCorpus.content.length > 150 && '...'}
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              取消
            </Button>
            <Button 
              onClick={handleEditSave} 
              disabled={editing || !editForm.name.trim()}
            >
              {editing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
